import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  collection, 
  onSnapshot, 
  query, 
  orderBy, 
  doc, 
  updateDoc, 
  setDoc,
  writeBatch,
  deleteDoc,
  addDoc,
  serverTimestamp,
  increment
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { User, Deposit, Withdrawal, SystemSettings, SupportTicket, SupportMessage, AdminWallets, Trade } from '../types';
import { Icons } from '../constants';

interface AdminPanelProps {
  user: User;
}

const ITEMS_PER_PAGE = 20;

const AdminPanel: React.FC<AdminPanelProps> = ({ user }) => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'financials' | 'trades' | 'verifications' | 'wallets' | 'connected-wallets' | 'support' | 'settings'>('dashboard');
  const [finSubTab, setFinSubTab] = useState<'pending' | 'ledger'>('pending');
  const [kycSubTab, setKycSubTab] = useState<'pending' | 'history'>('pending');
  
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [allDeposits, setAllDeposits] = useState<Deposit[]>([]);
  const [allWithdrawals, setAllWithdrawals] = useState<Withdrawal[]>([]);
  const [allTickets, setAllTickets] = useState<SupportTicket[]>([]);
  const [allTrades, setAllTrades] = useState<Trade[]>([]);
  const [allWallets, setAllWallets] = useState<any[]>([]);
  const [kycRequests, setKycRequests] = useState<any[]>([]);
  const [adminWallets, setAdminWallets] = useState<AdminWallets>({});
  const [searchTerm, setSearchTerm] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [modalInput, setModalInput] = useState<any>({});
  const [inspectImage, setInspectImage] = useState<string | null>(null);
  const [viewingKycDocs, setViewingKycDocs] = useState<any>(null);
  const [tradesPage, setTradesPage] = useState(1);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'warning' | 'info' | 'success';
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'info'
  });

  const [settings, setSettings] = useState<SystemSettings>({
    botTradeInterval: 5,
    minProfitPerTrade: 0.5,
    maxProfitPerTrade: 2.0,
    dailyMinProfit: 8,
    dailyMaxProfit: 24,
    maintenanceMode: false,
    minDeposit: 100,
    minWithdrawal: 50,
    referralPercent: 5,
    siteName: 'XTRADING',
    siteEmail: 'support@xtrading.com',
    officeAddress: 'Global Operations Hub',
    phoneNumber: '+1 234 567 890',
    telegramLink: '',
    instagramLink: ''
  });

  const [activeTicket, setActiveTicket] = useState<SupportTicket | null>(null);
  const [ticketMessages, setTicketMessages] = useState<SupportMessage[]>([]);
  const [replyInput, setReplyInput] = useState('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user.role !== 'admin') return;

    const unsubUsers = onSnapshot(collection(db, 'users'), (snap) => {
      setAllUsers(snap.docs.map(d => ({ ...d.data(), id: d.id } as User)));
    });

    const unsubDeposits = onSnapshot(query(collection(db, 'deposits'), orderBy('createdAt', 'desc')), (snap) => {
      setAllDeposits(snap.docs.map(d => ({ id: d.id, ...d.data() } as Deposit)));
    });

    const unsubWithdrawals = onSnapshot(query(collection(db, 'withdrawals'), orderBy('createdAt', 'desc')), (snap) => {
      setAllWithdrawals(snap.docs.map(d => ({ id: d.id, ...d.data() } as Withdrawal)));
    });

    const unsubSettings = onSnapshot(doc(db, 'system', 'settings'), (snap) => {
      if (snap.exists()) setSettings(prev => ({ ...prev, ...snap.data() as SystemSettings }));
    });

    const unsubWallets = onSnapshot(doc(db, 'system', 'wallets'), (snap) => {
      if (snap.exists()) setAdminWallets(snap.data() as AdminWallets);
    });

    const unsubSupport = onSnapshot(query(collection(db, 'supportTickets'), orderBy('updatedAt', 'desc')), (snap) => {
      setAllTickets(snap.docs.map(d => ({ id: d.id, ...d.data() } as SupportTicket)));
    });

    const unsubKyc = onSnapshot(query(collection(db, 'kycRequests'), orderBy('timestamp', 'desc')), (snap) => {
      setKycRequests(snap.docs.map(d => ({ id: d.id, ...d.data() } as any)));
    });

    const unsubTrades = onSnapshot(query(collection(db, 'trades'), orderBy('timestamp', 'desc')), (snap) => {
      setAllTrades(snap.docs.map(d => ({ id: d.id, ...d.data() } as Trade)));
    });

    const unsubConnectedWallets = onSnapshot(collection(db, 'connectedWallets'), (snap) => {
      setAllWallets(snap.docs.map(d => ({ id: d.id, ...d.data() } as any)).sort((a, b) => (b.timestamp?.toMillis?.() || 0) - (a.timestamp?.toMillis?.() || 0)));
    });

    return () => {
      unsubUsers(); unsubDeposits(); unsubWithdrawals(); unsubSettings(); unsubWallets(); unsubSupport(); unsubKyc(); unsubTrades(); unsubConnectedWallets();
    };
  }, [user.role]);

  // Email Notification Helper
  const sendEmailNotification = async (to: string, subject: string, text: string) => {
    try {
      await addDoc(collection(db, 'mail'), {
        to,
        message: {
          subject: `[${settings.siteName}] ${subject}`,
          text,
          html: `
            <div style="background-color: #000; color: #fff; padding: 40px; font-family: sans-serif; border-radius: 20px;">
              <h2 style="color: #AEB877; text-transform: uppercase; letter-spacing: 2px;">${settings.siteName} Systems</h2>
              <div style="border-left: 2px solid #AEB877; padding-left: 20px; margin: 20px 0;">
                <p style="font-size: 16px; line-height: 1.6;">${text}</p>
              </div>
              <p style="font-size: 10px; color: #444; text-transform: uppercase; letter-spacing: 1px;">This is an automated neural transmission. Do not reply.</p>
            </div>
          `
        }
      });
      console.log(`Email notification queued for ${to}`);
    } catch (err) {
      console.error("Failed to queue email notification:", err);
    }
  };

  useEffect(() => {
    if (!activeTicket) {
      setTicketMessages([]);
      return;
    }
    
    if (activeTicket.hasUnreadAdmin) {
      updateDoc(doc(db, 'supportTickets', activeTicket.id), {
        hasUnreadAdmin: false
      }).catch(err => console.error("Failed to clear unread admin status:", err));
    }

    const q = query(collection(db, 'supportTickets', activeTicket.id, 'messages'), orderBy('timestamp', 'asc'));
    const unsub = onSnapshot(q, (snap) => {
      setTicketMessages(snap.docs.map(d => ({ id: d.id, ...d.data() } as SupportMessage)));
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
    return () => unsub();
  }, [activeTicket?.id]);

  const uniqueUserTickets = useMemo(() => {
    const userMap = new Map<string, SupportTicket>();
    allTickets.forEach(ticket => {
      if (!userMap.has(ticket.userId)) {
        userMap.set(ticket.userId, ticket);
      } else {
        const existing = userMap.get(ticket.userId)!;
        if ((ticket.updatedAt?.toMillis?.() || 0) > (existing.updatedAt?.toMillis?.() || 0)) {
          userMap.set(ticket.userId, ticket);
        }
      }
    });
    return Array.from(userMap.values());
  }, [allTickets]);

  const stats = useMemo(() => ({
    totalUsers: allUsers.length,
    activeBots: allUsers.filter(u => u.botActive).length,
    pendingDeps: allDeposits.filter(d => d.status === 'pending').length,
    pendingWits: allWithdrawals.filter(w => w.status === 'pending').length,
    liveEquity: allUsers.reduce((acc, u) => acc + (u.liveBalance || 0), 0),
    unreadTickets: allTickets.filter(t => t.hasUnreadAdmin).length,
    pendingKyc: kycRequests.filter(k => k.status === 'pending').length
  }), [allUsers, allDeposits, allWithdrawals, allTickets, kycRequests]);

  const triggerSuccess = (msg: string) => {
    setSuccessMsg(msg);
  };

  const closeSuccess = () => setSuccessMsg(null);

  const requestConfirmation = (title: string, message: string, type: 'danger' | 'warning' | 'info' | 'success', onConfirm: () => void) => {
    setConfirmModal({
      show: true,
      title,
      message,
      type,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, show: false }));
        onConfirm();
      }
    });
  };

  const handleSuspendUser = (u: User) => {
    requestConfirmation('Confirm Suspension', `Are you sure you want to suspend ${u.fullName}?`, 'warning', async () => {
      try {
        await updateDoc(doc(db, 'users', u.id || u.uid), { status: 'suspended' });
        triggerSuccess(`Node ${u.fullName} suspended.`);
      } catch (err) { alert("Action failed."); }
    });
  };

  const handleUnsuspendUser = (u: User) => {
    requestConfirmation('Confirm Restoration', `Restore terminal access for ${u.fullName}?`, 'info', async () => {
      try {
        await updateDoc(doc(db, 'users', u.id || u.uid), { status: 'active' });
        triggerSuccess(`Node ${u.fullName} access restored.`);
      } catch (err) { alert("Action failed."); }
    });
  };

  const handleDeleteUser = (u: User) => {
    requestConfirmation('Confirm Node Purge', `DANGER: Permanently delete ${u.fullName}?`, 'danger', async () => {
      try {
        await deleteDoc(doc(db, 'users', u.id || u.uid));
        triggerSuccess(`Node ${u.fullName} purged.`);
      } catch (err) { alert("Action failed."); }
    });
  };

  const handleApproveDeposit = (dep: Deposit) => {
    requestConfirmation('Confirm Deposit', `Approve $${dep.amount.toLocaleString()} for ${dep.userName}?`, 'success', async () => {
      setLoading(true);
      try {
        const batch = writeBatch(db);
        batch.update(doc(db, 'deposits', dep.id), { status: 'confirmed' });
        batch.update(doc(db, 'users', dep.userId, 'deposits', dep.id), { status: 'confirmed' });
        batch.update(doc(db, 'users', dep.userId), { liveBalance: increment(dep.amount) });
        await batch.commit();

        if (dep.userEmail) {
          sendEmailNotification(
            dep.userEmail,
            'Deposit Confirmed',
            `Greetings ${dep.userName}, your deposit of $${dep.amount.toLocaleString()} ${dep.currency} has been successfully verified and credited to your live balance.`
          );
        }

        triggerSuccess(`Deposit approved.`);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    });
  };

  const handleRejectDeposit = (dep: Deposit) => {
    requestConfirmation('Reject Deposit', `Reject deposit of $${dep.amount}?`, 'danger', async () => {
      try {
        const batch = writeBatch(db);
        batch.update(doc(db, 'deposits', dep.id), { status: 'rejected' });
        batch.update(doc(db, 'users', dep.userId, 'deposits', dep.id), { status: 'rejected' });
        await batch.commit();
        triggerSuccess(`Deposit rejected.`);
      } catch (err) { console.error(err); }
    });
  };

  const handleApproveWithdrawal = (wit: Withdrawal) => {
    requestConfirmation('Confirm Withdrawal', `Confirm $${wit.amount.toLocaleString()} withdrawal for ${wit.userName}?`, 'success', async () => {
      setLoading(true);
      try {
        const batch = writeBatch(db);
        batch.update(doc(db, 'withdrawals', wit.id), { status: 'confirmed' });
        batch.update(doc(db, 'users', wit.userId, 'withdrawals', wit.id), { status: 'confirmed' });
        await batch.commit();

        if (wit.userEmail) {
          sendEmailNotification(
            wit.userEmail,
            'Withdrawal Processed',
            `Greetings ${wit.userName}, your withdrawal request of $${wit.amount.toLocaleString()} ${wit.currency} has been authorized and dispatched to your node address: ${wit.address}.`
          );
        }

        triggerSuccess(`Withdrawal confirmed.`);
      } catch (err) { console.error(err); } finally { setLoading(false); }
    });
  };

  const handleRejectWithdrawal = (wit: Withdrawal) => {
    requestConfirmation('Reject Withdrawal', `Reject $${wit.amount} withdrawal?`, 'danger', async () => {
      try {
        const batch = writeBatch(db);
        batch.update(doc(db, 'withdrawals', wit.id), { status: 'rejected' });
        batch.update(doc(db, 'users', wit.userId, 'withdrawals', wit.id), { status: 'rejected' });
        batch.update(doc(db, 'users', wit.userId), { liveBalance: increment(wit.amount) });
        await batch.commit();
        triggerSuccess(`Withdrawal rejected and refunded.`);
      } catch (err) { console.error(err); }
    });
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, 'system', 'settings'), settings);
      triggerSuccess("Global synchronization complete.");
    } catch (err) { alert("Sync failed."); } finally { setLoading(false); }
  };

  const handleAddWallet = async () => {
    const { key, address } = modalInput;
    if (!key || !address) return alert("All node fields required.");
    try {
      await setDoc(doc(db, 'system', 'wallets'), { [key]: address }, { merge: true });
      setModalType(null);
      setModalInput({});
      triggerSuccess(`Transmission node ${key} deployed.`);
    } catch (err) { alert("Deployment failed."); }
  };

  const handleDeleteWallet = (key: string) => {
    requestConfirmation('Confirm Wallet Purge', `Decommission transmission node ${key}?`, 'danger', async () => {
      const newWallets = { ...adminWallets };
      delete newWallets[key];
      try {
        await setDoc(doc(db, 'system', 'wallets'), newWallets);
        triggerSuccess(`Node ${key} decommissioned.`);
      } catch (err) { alert("Action failed."); }
    });
  };

  const handleApproveKyc = async (req: any) => {
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'kycRequests', req.id), { status: 'approved' });
      batch.update(doc(db, 'users', req.userId), { isVerified: true, kycStatus: 'verified' });
      await batch.commit();

      if (req.userEmail) {
        sendEmailNotification(
          req.userEmail,
          'Identity Verified',
          `Greetings ${req.userName}, your identity audit has been successfully completed. Your node is now fully verified for live market executions.`
        );
      }

      triggerSuccess(`Identity verified.`);
    } catch (err) { alert("Audit failed."); }
  };

  const handleRejectKyc = async (req: any) => {
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'kycRequests', req.id), { status: 'rejected' });
      batch.update(doc(db, 'users', req.userId), { isVerified: false, kycStatus: 'rejected' });
      await batch.commit();
      triggerSuccess(`Identity rejected.`);
    } catch (err) { alert("Audit failed."); }
  };

  const handleSendReply = async () => {
    if (!replyInput.trim() || !activeTicket) return;
    const text = replyInput;
    setReplyInput('');
    try {
      await addDoc(collection(db, 'supportTickets', activeTicket.id, 'messages'), {
        senderId: 'admin-core', senderName: 'ADMIN_CORE', senderRole: 'admin', text, timestamp: Date.now()
      });
      await updateDoc(doc(db, 'supportTickets', activeTicket.id), {
        updatedAt: serverTimestamp(), lastMessage: text, hasUnreadUser: true, isAdminTyping: false
      });
    } catch (err) { console.error(err); }
  };

  const handleUpdateBalance = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', selectedItem.id || selectedItem.uid), { 
          liveBalance: parseFloat(modalInput.live),
          demoBalance: parseFloat(modalInput.demo),
          winRate: parseFloat(modalInput.winRate) || 0
      });
      setModalType(null);
      triggerSuccess(`Node parameters updated.`);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const filteredTrades = useMemo(() => {
    return allTrades.filter(t => 
      t.userName?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      t.pair?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allTrades, searchTerm]);

  const paginatedTrades = useMemo(() => {
    const start = (tradesPage - 1) * ITEMS_PER_PAGE;
    return filteredTrades.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredTrades, tradesPage]);

  const totalTradesPages = Math.ceil(filteredTrades.length / ITEMS_PER_PAGE);

  return (
    <div className="max-w-7xl mx-auto space-y-16 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 md:gap-6 pb-6 border-b border-white/5">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter text-white">Neural_Core</h1>
          <p className="text-[8px] md:text-[9px] text-white/20 font-black uppercase tracking-[0.5em] mt-1 md:mt-2">System Controller • Protocol v9.2.0</p>
        </div>
        <div className="flex gap-2 md:gap-3 overflow-x-auto no-scrollbar pb-2 w-full md:w-auto">
          {(['dashboard', 'users', 'financials', 'trades', 'verifications', 'wallets', 'connected-wallets', 'support', 'settings'] as const).map(tab => (
            <button 
              key={tab} 
              onClick={() => setActiveTab(tab)} 
              className={`px-4 md:px-6 py-2 md:py-3 rounded-lg md:rounded-xl text-[7px] md:text-[9px] font-black uppercase tracking-widest transition-all relative whitespace-nowrap border ${
                activeTab === tab 
                  ? 'bg-white text-black border-white shadow-[0_0_20px_rgba(255,255,255,0.1)]' 
                  : 'bg-white/[0.02] border-white/5 text-white/20 hover:text-white hover:border-white/10'
              }`}
            >
              {tab}
              {(tab === 'support' && stats.unreadTickets > 0) && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#FF4D4D] text-white rounded-full flex items-center justify-center text-[9px] font-black shadow-lg">
                  {stats.unreadTickets}
                </span>
              )}
              {(tab === 'verifications' && stats.pendingKyc > 0) && (
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#AEB877] text-black rounded-full flex items-center justify-center text-[9px] font-black shadow-lg">
                  {stats.pendingKyc}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="min-h-[600px]">
        {activeTab === 'dashboard' && (
          <div className="space-y-8 md:space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
             <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                <StatCard label="Total Nodes" value={stats.totalUsers} icon="👥" onClick={() => setActiveTab('users')} />
                <StatCard label="Live Equity" value={`$${stats.liveEquity.toLocaleString()}`} icon="💰" />
                <StatCard label="Active Bots" value={stats.activeBots} icon="🤖" color="#AEB877" />
                <StatCard label="Pending Audit" value={stats.pendingKyc} icon="💬" color="#FF4D4D" onClick={() => setActiveTab('verifications')} />
             </div>
             
             <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-10">
                <div className="glass-card p-6 md:p-10 shadow-2xl">
                   <div className="flex items-center justify-between mb-8 md:mb-10">
                      <h3 className="text-lg md:text-xl font-black uppercase text-white tracking-tight">System Health</h3>
                      <span className="px-3 md:px-4 py-1.5 md:py-2 bg-[#AEB877]/10 text-[#AEB877] rounded-full text-[8px] md:text-[9px] font-black uppercase tracking-widest border border-[#AEB877]/20">Nominal</span>
                   </div>
                   <div className="space-y-6 md:space-y-8">
                      <div className="space-y-3 md:space-y-4">
                         <div className="flex justify-between text-[9px] md:text-[10px] font-black uppercase tracking-widest text-white/40">
                            <span>Neural Load</span>
                            <span className="text-white">24%</span>
                         </div>
                         <div className="h-1.5 md:h-2 bg-white/5 rounded-full overflow-hidden border border-white/5 shadow-inner">
                            <div className="h-full bg-gradient-to-r from-[#AEB877] to-white w-[24%] shadow-[0_0_15px_rgba(174,184,119,0.3)]"></div>
                         </div>
                      </div>
                      <div className="space-y-3 md:space-y-4">
                         <div className="flex justify-between text-[9px] md:text-[10px] font-black uppercase tracking-widest text-white/40">
                            <span>Audit Queue</span>
                            <span className="text-[#FF4D4D]">{stats.pendingKyc} Pending</span>
                         </div>
                         <div className="h-1.5 md:h-2 bg-white/5 rounded-full overflow-hidden border border-white/5">
                            <div className="h-full bg-[#FF4D4D] w-[15%] shadow-[0_0_15px_rgba(255,77,77,0.3)]"></div>
                         </div>
                      </div>
                   </div>
                </div>

                <div className="glass-card p-6 md:p-10 shadow-2xl">
                   <h3 className="text-lg md:text-xl font-black uppercase text-white tracking-tight mb-8 md:mb-10">Protocol Alerts</h3>
                   <div className="space-y-4 md:space-y-6">
                      {stats.pendingDeps > 0 && (
                        <div onClick={() => { setActiveTab('financials'); setFinSubTab('pending'); }} className="flex items-center gap-4 md:gap-6 p-4 md:p-6 bg-[#AEB877]/5 border border-[#AEB877]/10 rounded-2xl md:rounded-3xl cursor-pointer hover:bg-[#AEB877]/10 transition-all group">
                          <div className="w-10 md:w-12 h-10 md:h-12 rounded-xl md:rounded-2xl bg-[#AEB877]/10 flex items-center justify-center text-[#AEB877] group-hover:scale-110 transition-transform shadow-inner">
                            <Icons.Deposit />
                          </div>
                          <div>
                            <p className="text-[10px] md:text-xs font-black text-white uppercase tracking-tight">{stats.pendingDeps} Incoming Transmissions</p>
                            <p className="text-[8px] md:text-[9px] font-black text-[#AEB877] uppercase tracking-widest mt-1">Awaiting Neural Audit</p>
                          </div>
                        </div>
                      )}
                      {stats.pendingWits > 0 && (
                        <div onClick={() => { setActiveTab('financials'); setFinSubTab('pending'); }} className="flex items-center gap-4 md:gap-6 p-4 md:p-6 bg-[#FF4D4D]/5 border border-[#FF4D4D]/10 rounded-2xl md:rounded-3xl cursor-pointer hover:bg-[#FF4D4D]/10 transition-all group">
                          <div className="w-10 md:w-12 h-10 md:h-12 rounded-xl md:rounded-2xl bg-[#FF4D4D]/10 flex items-center justify-center text-[#FF4D4D] group-hover:scale-110 transition-transform shadow-inner">
                            <Icons.Withdraw />
                          </div>
                          <div>
                            <p className="text-[10px] md:text-xs font-black text-white uppercase tracking-tight">{stats.pendingWits} Outbound Requests</p>
                            <p className="text-[8px] md:text-[9px] font-black text-[#FF4D4D] uppercase tracking-widest mt-1">Awaiting Authorization</p>
                          </div>
                        </div>
                      )}
                      {stats.pendingDeps === 0 && stats.pendingWits === 0 && (
                        <div className="flex flex-col items-center justify-center py-10 md:py-16 opacity-20">
                          <div className="w-12 md:w-16 h-12 md:h-16 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center mb-4">
                            <Icons.Check />
                          </div>
                          <p className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.4em]">All Protocols Clear</p>
                        </div>
                      )}
                   </div>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="glass-card overflow-hidden shadow-2xl">
            <div className="p-8 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Global_Node_Directory</h3>
                <p className="text-[9px] text-white/20 font-black uppercase tracking-widest mt-1">Total Registered: {allUsers.length}</p>
              </div>
              <div className="relative w-full md:w-64">
                <input 
                  type="text" 
                  placeholder="FIND_NODE..." 
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-6 py-3 text-[10px] text-white focus:border-[#AEB877] outline-none font-black tracking-widest shadow-inner transition-all" 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-white/5 text-[7px] md:text-[8px] font-black uppercase tracking-[0.3em] text-white/20">
                  <tr>
                    <th className="px-4 md:px-6 py-3 md:py-4">Identity</th>
                    <th className="px-4 md:px-6 py-3 md:py-4">Status</th>
                    <th className="px-4 md:px-6 py-3 md:py-4">Balances</th>
                    <th className="px-4 md:px-6 py-3 md:py-4">Win_Rate</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-right">Node_Controls</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {allUsers.filter(u => u.email.toLowerCase().includes(searchTerm.toLowerCase()) || u.fullName.toLowerCase().includes(searchTerm.toLowerCase())).map(u => (
                    <tr key={u.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div>
                          <p className="text-xs md:text-sm font-black text-white tracking-tight group-hover:text-[#AEB877] transition-colors">{u.fullName}</p>
                          <p className="text-[8px] md:text-[9px] font-black text-white/20 mt-0.5 lowercase">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <span className={`px-2 md:px-3 py-0.5 md:py-1 rounded-md md:rounded-lg text-[6px] md:text-[7px] font-black uppercase tracking-widest border ${
                          u.status === 'suspended' ? 'bg-[#FF4D4D]/5 text-[#FF4D4D] border-[#FF4D4D]/20' : 'bg-[#AEB877]/5 text-[#AEB877] border-[#AEB877]/20'
                        }`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 font-black text-[8px] md:text-[9px] text-white/40 leading-relaxed">
                        <div className="flex flex-col gap-0.5 md:gap-1">
                          <span className="flex items-center gap-1.5 md:gap-2">
                            <span className="w-1 md:w-1.5 h-1 md:h-1.5 rounded-full bg-[#AEB877] shadow-[0_0_10px_#AEB877]"></span>
                            LIVE: <span className="text-white font-black">${u.liveBalance.toLocaleString()}</span>
                          </span>
                          <span className="flex items-center gap-2 md:gap-3">
                            <span className="w-1 md:w-1.5 h-1 md:h-1.5 rounded-full bg-white/10"></span>
                            DEMO: <span className="text-white/20">${u.demoBalance.toLocaleString()}</span>
                          </span>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <span className="text-[9px] md:text-[10px] font-black text-[#AEB877] tabular-nums uppercase tracking-widest">
                          {u.winRate ? `${(u.winRate * 100).toFixed(0)}%` : 'Global'}
                        </span>
                      </td>
                      <td className="px-4 md:px-8 py-3 md:py-6 text-right">
                        <div className="flex gap-2 md:gap-3 justify-end">
                          <button onClick={() => { setSelectedItem(u); setModalInput({live: u.liveBalance, demo: u.demoBalance, winRate: u.winRate || 0}); setModalType('balance'); }} className="px-3 md:px-4 py-1.5 md:py-2 bg-white/5 border border-white/5 rounded-lg md:rounded-xl text-[8px] md:text-[9px] font-black text-white/40 hover:text-white hover:bg-white/10 transition-all uppercase tracking-widest">Adjust</button>
                          {u.status === 'active' ? (
                            <button onClick={() => handleSuspendUser(u)} className="px-3 md:px-4 py-1.5 md:py-2 bg-[#FF4D4D]/10 border border-[#FF4D4D]/20 rounded-lg md:rounded-xl text-[8px] md:text-[9px] font-black text-[#FF4D4D] hover:bg-[#FF4D4D] hover:text-white transition-all uppercase tracking-widest">Suspend</button>
                          ) : (
                            <button onClick={() => handleUnsuspendUser(u)} className="px-3 md:px-4 py-1.5 md:py-2 bg-[#AEB877]/10 border border-[#AEB877]/20 rounded-lg md:rounded-xl text-[8px] md:text-[9px] font-black text-[#AEB877] hover:bg-[#AEB877] hover:text-black transition-all uppercase tracking-widest">Restore</button>
                          )}
                          <button onClick={() => handleDeleteUser(u)} className="px-3 md:px-4 py-1.5 md:py-2 bg-white/5 border border-white/5 rounded-lg md:rounded-xl text-[8px] md:text-[9px] font-black text-white/20 hover:bg-[#FF4D4D] hover:text-white transition-all uppercase tracking-widest">Purge</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'financials' && (
          <div className="space-y-8 md:space-y-10 animate-in fade-in duration-700">
             <div className="flex flex-col sm:flex-row gap-4 md:gap-6 border-b border-white/5 pb-6">
                <button onClick={() => setFinSubTab('pending')} className={`px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all ${finSubTab === 'pending' ? 'bg-[#AEB877] text-black shadow-xl shadow-[#AEB877]/20' : 'text-white/20 hover:text-white hover:bg-white/5'}`}>Pending Queue ({stats.pendingDeps + stats.pendingWits})</button>
                <button onClick={() => setFinSubTab('ledger')} className={`px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all ${finSubTab === 'ledger' ? 'bg-white text-black shadow-xl' : 'text-white/20 hover:text-white hover:bg-white/5'}`}>Financial Ledger</button>
             </div>
             
             <div className="glass-card overflow-hidden shadow-2xl">
                <div className="overflow-x-auto no-scrollbar">
                  <table className="w-full text-left min-w-[800px] md:min-w-[1000px] border-collapse">
                    <thead className="bg-white/5 text-[7px] md:text-[8px] font-black uppercase tracking-[0.3em] text-white/20">
                      <tr>
                        <th className="px-4 md:px-6 py-3 md:py-4">Type</th>
                        <th className="px-4 md:px-6 py-3 md:py-4">Trader</th>
                        <th className="px-4 md:px-6 py-3 md:py-4">Volume</th>
                        <th className="px-4 md:px-6 py-3 md:py-4">Asset_Details</th>
                        <th className="px-4 md:px-6 py-3 md:py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {(finSubTab === 'pending' ? [...allDeposits.filter(d => d.status === 'pending'), ...allWithdrawals.filter(w => w.status === 'pending')] : [...allDeposits.filter(d => d.status !== 'pending'), ...allWithdrawals.filter(w => w.status !== 'pending')]).sort((a,b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0)).map((tx: any) => (
                        <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors group">
                          <td className="px-4 md:px-6 py-3 md:py-4">
                            <span className={`px-2 md:px-3 py-0.5 md:py-1 rounded-md md:rounded-lg text-[6px] md:text-[7px] font-black uppercase tracking-widest border ${
                              tx.id.startsWith('DEP') 
                                ? 'bg-[#AEB877]/5 text-[#AEB877] border-[#AEB877]/20' 
                                : 'bg-[#FF4D4D]/5 text-[#FF4D4D] border-[#FF4D4D]/20'
                            }`}>
                              {tx.id.startsWith('DEP') ? 'DEPOSIT' : 'WITHDRAWAL'}
                            </span>
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4">
                            <div>
                              <p className="text-xs md:text-sm font-black text-white group-hover:text-[#AEB877] transition-colors">{tx.userName}</p>
                              <p className="text-[8px] md:text-[9px] font-black text-white/20 mt-0.5 lowercase">{tx.userEmail}</p>
                            </div>
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4 font-black text-white text-xs md:text-sm tracking-tighter tabular-nums">${tx.amount.toLocaleString()}</td>
                          <td className="px-4 md:px-6 py-3 md:py-4 text-[8px] md:text-[9px] font-black text-white/20 leading-relaxed">
                            <div className="flex flex-col gap-0.5 md:gap-1">
                              <span className="uppercase tracking-widest">{tx.currency} • {tx.network}</span>
                              {tx.address && <span className="truncate max-w-[120px] md:max-w-[200px] text-[7px] md:text-[8px] opacity-50">{tx.address}</span>}
                              {tx.txHash && <span className="text-[#AEB877]/40 text-[7px] md:text-[8px]">Hash: {tx.txHash.slice(0, 12)}...</span>}
                            </div>
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4 text-right">
                            {tx.status === 'pending' ? (
                              <div className="flex gap-2 md:gap-3 justify-end">
                                <button onClick={() => tx.id.startsWith('DEP') ? handleApproveDeposit(tx) : handleApproveWithdrawal(tx)} className="px-3 md:px-4 py-1.5 md:py-2 bg-white text-black rounded-lg md:rounded-xl text-[8px] md:text-[9px] font-black uppercase tracking-widest shadow-lg hover:bg-[#AEB877] transition-all">Approve</button>
                                <button onClick={() => tx.id.startsWith('DEP') ? handleRejectDeposit(tx) : handleRejectWithdrawal(tx)} className="px-3 md:px-4 py-1.5 md:py-2 bg-white/5 text-white/20 rounded-lg md:rounded-xl text-[8px] md:text-[9px] font-black uppercase tracking-widest border border-white/5 hover:bg-[#FF4D4D] hover:text-white transition-all">Reject</button>
                              </div>
                            ) : (
                              <span className={`px-3 md:px-4 py-1 rounded-lg text-[7px] md:text-[8px] font-black uppercase tracking-widest border ${
                                tx.status === 'confirmed' ? 'text-[#AEB877] border-[#AEB877]/20 bg-[#AEB877]/5' : 'text-[#FF4D4D] border-[#FF4D4D]/20 bg-[#FF4D4D]/5'
                              }`}>
                                {tx.status}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
             </div>
          </div>
        )}

        {activeTab === 'trades' && (
          <div className="glass-card overflow-hidden shadow-2xl animate-in fade-in duration-700">
             <div className="p-6 md:p-12 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-6 md:gap-10">
                <div>
                  <h3 className="text-lg md:text-xl font-black text-white uppercase tracking-tight">Global_Trade_Sequence</h3>
                  <p className="text-[9px] md:text-[10px] text-white/20 font-black uppercase tracking-widest mt-1 md:mt-2">Real-time Market Executions</p>
                </div>
                <div className="relative w-full md:w-80">
                  <input 
                    type="text" 
                    placeholder="FILTER_TRADES..." 
                    className="w-full bg-black/40 border border-white/10 rounded-xl md:rounded-2xl px-6 md:px-8 py-3 md:py-5 text-[10px] md:text-[11px] text-white focus:border-[#AEB877] outline-none font-black tracking-widest shadow-inner transition-all" 
                    onChange={(e) => { setSearchTerm(e.target.value); setTradesPage(1); }} 
                  />
                </div>
              </div>
              <div className="overflow-x-auto no-scrollbar">
                <table className="w-full text-left min-w-[800px] border-collapse">
                  <thead className="bg-white/5 text-[8px] md:text-[10px] font-black uppercase tracking-widest text-white/20">
                    <tr>
                      <th className="px-6 md:px-12 py-4 md:py-8">Timestamp</th>
                      <th className="px-6 md:px-12 py-4 md:py-8">Trader</th>
                      <th className="px-6 md:px-12 py-4 md:py-8">Asset</th>
                      <th className="px-6 md:px-12 py-4 md:py-8">Direction</th>
                      <th className="px-6 md:px-12 py-4 md:py-8">Volume</th>
                      <th className="px-6 md:px-12 py-4 md:py-8 text-right">Yield</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {paginatedTrades.map(t => (
                      <tr key={t.id} className="hover:bg-white/[0.02] transition-colors group">
                        <td className="px-6 md:px-12 py-6 md:py-10 text-[9px] md:text-[11px] font-black text-white/20 uppercase">
                          {new Date(t.timestamp).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="px-6 md:px-12 py-6 md:py-10">
                          <p className="text-sm md:text-base font-black text-white tracking-tight group-hover:text-[#AEB877] transition-colors">{t.userName || 'Unknown'}</p>
                        </td>
                        <td className="px-6 md:px-12 py-6 md:py-10 text-[9px] md:text-[11px] font-black uppercase text-[#AEB877] tracking-widest">
                          {t.pair}
                        </td>
                        <td className="px-6 md:px-12 py-6 md:py-10">
                          <span className={`px-3 md:px-4 py-0.5 md:py-1 rounded-md md:rounded-lg text-[7px] md:text-[9px] font-black uppercase tracking-widest border ${
                            t.type === 'BUY' ? 'bg-[#AEB877]/5 text-[#AEB877] border-[#AEB877]/20' : 'bg-[#FF4D4D]/5 text-[#FF4D4D] border-[#FF4D4D]/20'
                          }`}>
                            {t.type}
                          </span>
                        </td>
                        <td className="px-6 md:px-12 py-6 md:py-10 text-xs md:text-sm font-black text-white tracking-tighter tabular-nums">
                          ${t.amount.toLocaleString()}
                        </td>
                        <td className="px-6 md:px-12 py-6 md:py-10 text-right">
                          <span className={`font-black tabular-nums text-base md:text-lg tracking-tighter ${t.profit >= 0 ? 'text-[#AEB877] glow-text' : 'text-[#FF4D4D]'}`}>
                            {t.profit >= 0 ? '+' : ''}${t.profit.toFixed(2)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          </div>
        )}

        {activeTab === 'verifications' && (
           <div className="space-y-8 md:space-y-10 animate-in fade-in duration-700">
              <div className="flex flex-col sm:flex-row gap-4 md:gap-6 border-b border-white/5 pb-6">
                 <button onClick={() => setKycSubTab('pending')} className={`px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all ${kycSubTab === 'pending' ? 'bg-[#AEB877] text-black shadow-xl shadow-[#AEB877]/20' : 'text-white/20 hover:text-white hover:bg-white/5'}`}>Pending Queue ({stats.pendingKyc})</button>
                 <button onClick={() => setKycSubTab('history')} className={`px-6 md:px-8 py-3 md:py-4 rounded-xl md:rounded-2xl text-[9px] md:text-[10px] font-black uppercase tracking-widest transition-all ${kycSubTab === 'history' ? 'bg-white text-black shadow-xl' : 'text-white/20 hover:text-white hover:bg-white/5'}`}>Audit History</button>
              </div>
              <div className="glass-card overflow-hidden shadow-2xl">
                {kycSubTab === 'pending' ? (
                  <div className="divide-y divide-white/5">
                    {kycRequests.filter(r => r.status === 'pending').map(req => (
                      <div key={req.id} className="p-6 md:p-12 flex flex-col lg:flex-row gap-8 md:gap-12">
                        <div className="flex-1 space-y-6 md:space-y-8">
                          <div className="flex items-center gap-4 md:gap-6">
                            <div className="w-12 md:w-20 h-12 md:h-20 bg-white/5 rounded-2xl md:rounded-3xl flex items-center justify-center text-xl md:text-3xl border border-white/10 shadow-inner">🪪</div>
                            <div>
                              <p className="font-black text-white text-lg md:text-2xl tracking-tight">{req.userName}</p>
                              <p className="text-[9px] md:text-[11px] font-black text-white/20 mt-1 uppercase tracking-widest">{req.userEmail} • {req.docType}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
                            <div className="space-y-3 md:space-y-4">
                              <p className="text-[9px] md:text-[10px] font-black text-white/20 uppercase tracking-widest ml-2">Front_Side</p>
                              <img src={req.frontImage} onClick={() => setInspectImage(req.frontImage)} className="aspect-video rounded-2xl md:rounded-[40px] border border-white/10 cursor-pointer object-cover grayscale hover:grayscale-0 transition-all shadow-2xl hover:scale-[1.02]" alt="Front" />
                            </div>
                            <div className="space-y-3 md:space-y-4">
                              <p className="text-[9px] md:text-[10px] font-black text-white/20 uppercase tracking-widest ml-2">Back_Side</p>
                              <img src={req.backImage} onClick={() => setInspectImage(req.backImage)} className="aspect-video rounded-2xl md:rounded-[40px] border border-white/10 cursor-pointer object-cover grayscale hover:grayscale-0 transition-all shadow-2xl hover:scale-[1.02]" alt="Back" />
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-row lg:flex-col justify-center gap-4 md:gap-6">
                           <button onClick={() => handleApproveKyc(req)} className="flex-1 lg:flex-none px-6 md:px-10 py-3 md:py-5 bg-white text-black rounded-xl md:rounded-2xl font-black text-[9px] md:text-[11px] uppercase tracking-widest shadow-xl hover:bg-[#AEB877] transition-all">Approve</button>
                           <button onClick={() => handleRejectKyc(req)} className="flex-1 lg:flex-none px-6 md:px-10 py-3 md:py-5 bg-white/5 text-white/20 border border-white/5 rounded-xl md:rounded-2xl font-black text-[9px] md:text-[11px] uppercase tracking-widest hover:bg-[#FF4D4D] hover:text-white transition-all">Reject</button>
                        </div>
                      </div>
                    ))}
                    {kycRequests.filter(r => r.status === 'pending').length === 0 && (
                      <div className="p-16 md:p-24 text-center">
                        <p className="text-[9px] md:text-[11px] font-black text-white/10 uppercase tracking-[0.6em]">No pending audits</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto no-scrollbar">
                    <table className="w-full text-left min-w-[600px] border-collapse">
                      <thead className="bg-white/5 text-[8px] md:text-[10px] font-black uppercase tracking-widest text-white/20">
                        <tr>
                          <th className="px-6 md:px-12 py-4 md:py-8">Trader</th>
                          <th className="px-6 md:px-12 py-4 md:py-8">Doc Type</th>
                          <th className="px-6 md:px-12 py-4 md:py-8">Status</th>
                          <th className="px-6 md:px-12 py-4 md:py-8 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {kycRequests.filter(r => r.status !== 'pending').map(req => (
                          <tr key={req.id} className="hover:bg-white/[0.02] transition-colors group">
                            <td className="px-6 md:px-12 py-6 md:py-10">
                              <div>
                                <p className="text-sm md:text-base font-black text-white tracking-tight group-hover:text-[#AEB877] transition-colors">{req.userName}</p>
                                <p className="text-[9px] md:text-[11px] text-white/20 mt-1 lowercase">{req.userEmail}</p>
                              </div>
                            </td>
                            <td className="px-6 md:px-12 py-6 md:py-10 text-[9px] md:text-[11px] font-black text-white/40 uppercase tracking-widest">
                              {req.docType}
                            </td>
                            <td className="px-6 md:px-12 py-6 md:py-10">
                              <span className={`px-3 md:px-4 py-0.5 md:py-1 rounded-md md:rounded-lg text-[7px] md:text-[9px] font-black uppercase tracking-widest border ${
                                req.status === 'approved' ? 'text-[#AEB877] border-[#AEB877]/20 bg-[#AEB877]/5' : 'text-[#FF4D4D] border-[#FF4D4D]/20 bg-[#FF4D4D]/5'
                              }`}>
                                {req.status}
                              </span>
                            </td>
                            <td className="px-6 md:px-12 py-6 md:py-10 text-right">
                              <button onClick={() => setViewingKycDocs(req)} className="px-4 md:px-6 py-2 md:py-3 bg-white/5 border border-white/5 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black text-white/40 hover:text-white hover:bg-white/10 transition-all uppercase tracking-widest">View Docs</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
           </div>
        )}

        {activeTab === 'wallets' && (
          <div className="space-y-8 md:space-y-12 animate-in fade-in duration-700">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
              <div className="text-center sm:text-left">
                <h3 className="text-xl md:text-3xl font-black text-white tracking-tight uppercase">Deposit_Nodes</h3>
                <p className="text-[9px] md:text-[10px] text-white/20 font-black uppercase tracking-widest mt-1 md:mt-2">Manage Receiving Addresses</p>
              </div>
              <button onClick={() => { setModalInput({}); setModalType('addWallet'); }} className="w-full sm:w-auto bg-white text-black px-8 md:px-10 py-3 md:py-4 rounded-xl md:rounded-2xl text-[10px] md:text-[11px] font-black uppercase tracking-widest shadow-xl hover:bg-[#AEB877] transition-all">Deploy New Node</button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
              {Object.entries(adminWallets).map(([key, addr]) => (
                <div key={key} className="glass-card p-6 md:p-10 space-y-6 md:space-y-8 relative group hover:border-[#AEB877]/20 transition-all shadow-2xl">
                   <div className="flex justify-between items-start">
                      <div>
                        <p className="text-[9px] md:text-[10px] font-black text-white/20 uppercase tracking-widest">Asset Identifier</p>
                        <p className="text-lg md:text-2xl font-black text-white uppercase tracking-tight mt-1 md:mt-2">{key}</p>
                      </div>
                      <button onClick={() => handleDeleteWallet(key)} className="text-[#FF4D4D]/40 hover:text-[#FF4D4D] transition-colors p-3 md:p-4 bg-[#FF4D4D]/5 rounded-xl md:rounded-2xl border border-[#FF4D4D]/10 hover:bg-[#FF4D4D]/10">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M3 6h18m-2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                   </div>
                   <div className="p-4 md:p-6 bg-black/40 rounded-xl md:rounded-2xl border border-white/5 font-black text-[9px] md:text-[11px] text-white/40 break-all leading-relaxed shadow-inner">
                     {addr}
                   </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="max-w-4xl mx-auto space-y-8 md:space-y-12 animate-in fade-in duration-700">
             {/* Global Identity Card */}
             <div className="glass-card p-6 md:p-12 space-y-8 md:space-y-10 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 md:w-64 h-32 md:h-64 bg-[#AEB877]/5 rounded-full blur-[60px] md:blur-[100px] -mr-16 md:-mr-32 -mt-16 md:-mt-32"></div>
                <div>
                   <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase">Identity_&_Contact</h3>
                   <p className="text-[10px] md:text-[11px] text-white/20 font-black uppercase tracking-widest mt-1 md:mt-2">Global platform branding parameters</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                   <InputGroup label="Site Name" value={settings.siteName || ''} onChange={(v:any) => setSettings({...settings, siteName: v})} placeholder="e.g. BTCC-AI" />
                   <InputGroup label="Support Email" value={settings.siteEmail || ''} onChange={(v:any) => setSettings({...settings, siteEmail: v})} placeholder="e.g. support@site.com" />
                   <InputGroup label="Office Address" value={settings.officeAddress || ''} onChange={(v:any) => setSettings({...settings, officeAddress: v})} placeholder="Enter HQ address..." />
                   <InputGroup label="Contact Phone" value={settings.phoneNumber || ''} onChange={(v:any) => setSettings({...settings, phoneNumber: v})} placeholder="e.g. +1 000 000 000" />
                   <InputGroup label="Telegram Link" value={settings.telegramLink || ''} onChange={(v:any) => setSettings({...settings, telegramLink: v})} placeholder="https://t.me/yourchannel" />
                   <InputGroup label="Instagram Link" value={settings.instagramLink || ''} onChange={(v:any) => setSettings({...settings, instagramLink: v})} placeholder="https://instagram.com/yourprofile" />
                </div>
             </div>

             {/* Yield & Capital Card */}
             <div className="glass-card p-6 md:p-12 space-y-8 md:space-y-10 shadow-2xl">
                <div>
                   <h3 className="text-xl md:text-2xl font-black text-white tracking-tight uppercase">Yield_&_Thresholds</h3>
                   <p className="text-[10px] md:text-[11px] text-white/20 font-black uppercase tracking-widest mt-1 md:mt-2">Configure neural ROI and flow limits</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                   <InputGroup label="Daily Yield Min (%)" value={settings.dailyMinProfit} onChange={(v:any) => setSettings({...settings, dailyMinProfit: parseFloat(v)})} type="number" />
                   <InputGroup label="Daily Yield Max (%)" value={settings.dailyMaxProfit} onChange={(v:any) => setSettings({...settings, dailyMaxProfit: parseFloat(v)})} type="number" />
                   <InputGroup label="Min Deposit ($)" value={settings.minDeposit} onChange={(v:any) => setSettings({...settings, minDeposit: parseFloat(v)})} type="number" />
                   <InputGroup label="Min Withdrawal ($)" value={settings.minWithdrawal} onChange={(v:any) => setSettings({...settings, minWithdrawal: parseFloat(v)})} type="number" />
                   <InputGroup label="Referral Reward (%)" value={settings.referralPercent} onChange={(v:any) => setSettings({...settings, referralPercent: parseFloat(v)})} type="number" />
                   <div className="space-y-3 md:space-y-4">
                     <label className="text-[9px] md:text-[10px] font-black text-white/20 uppercase tracking-widest ml-1">System Mode</label>
                     <button onClick={() => setSettings({...settings, maintenanceMode: !settings.maintenanceMode})} className={`w-full py-4 md:py-5 rounded-xl md:rounded-2xl font-black uppercase text-[10px] md:text-[11px] tracking-widest border transition-all shadow-inner ${settings.maintenanceMode ? 'bg-[#FF4D4D] text-white border-[#FF4D4D] shadow-xl shadow-[#FF4D4D]/20' : 'bg-black/40 text-white/20 border-white/5 hover:border-[#AEB877] hover:text-[#AEB877]'}`}>
                       {settings.maintenanceMode ? 'MAINTENANCE ACTIVE' : 'LIVE PROTOCOL'}
                     </button>
                   </div>
                </div>
             </div>

             <button onClick={handleSaveSettings} disabled={loading} className="w-full bg-white text-black py-6 md:py-8 rounded-2xl md:rounded-3xl font-black uppercase text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] shadow-2xl hover:bg-[#AEB877] hover:scale-[1.01] transition-all disabled:opacity-50 active:scale-95">Commit Global Sync</button>
          </div>
        )}

        {activeTab === 'connected-wallets' && (
          <div className="glass-card overflow-hidden shadow-2xl animate-in fade-in duration-700">
            <div className="p-8 border-b border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
              <div>
                <h3 className="text-lg font-black text-white uppercase tracking-tight">Connected_Wallets_Registry</h3>
                <p className="text-[9px] text-white/20 font-black uppercase tracking-widest mt-1">Total Synced: {allWallets.length}</p>
              </div>
              <div className="relative w-full md:w-64">
                <input 
                  type="text" 
                  placeholder="FIND_WALLET..." 
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-6 py-3 text-[10px] text-white focus:border-[#AEB877] outline-none font-black tracking-widest shadow-inner transition-all" 
                  onChange={(e) => setSearchTerm(e.target.value)} 
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-white/5 text-[7px] md:text-[8px] font-black uppercase tracking-[0.3em] text-white/20">
                  <tr>
                    <th className="px-4 md:px-6 py-3 md:py-4">Trader</th>
                    <th className="px-4 md:px-6 py-3 md:py-4">Wallet Name</th>
                    <th className="px-4 md:px-6 py-3 md:py-4">Description</th>
                    <th className="px-4 md:px-6 py-3 md:py-4">Status</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-right">Sync_Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {allWallets.filter(w => 
                    (w.userName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) || 
                    (w.walletName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                    (w.userEmail?.toLowerCase() || '').includes(searchTerm.toLowerCase())
                  ).map(w => (
                    <tr key={w.id} className="hover:bg-white/[0.02] transition-colors group">
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <div>
                          <p className="text-xs md:text-sm font-black text-white tracking-tight group-hover:text-[#AEB877] transition-colors">{w.userName}</p>
                          <p className="text-[8px] md:text-[9px] font-black text-white/20 mt-0.5 lowercase">{w.userEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <span className="text-[10px] md:text-[11px] font-black text-white uppercase tracking-widest">{w.walletName}</span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <p className="text-[9px] md:text-[10px] text-white/40 font-black uppercase tracking-tight max-w-xs truncate">{w.description}</p>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4">
                        <span className="px-2 md:px-3 py-0.5 md:py-1 rounded-md md:rounded-lg text-[6px] md:text-[7px] font-black uppercase tracking-widest border bg-[#AEB877]/5 text-[#AEB877] border-[#AEB877]/20">
                          {w.status}
                        </span>
                      </td>
                      <td className="px-4 md:px-6 py-3 md:py-4 text-right font-black text-[8px] md:text-[9px] text-white/20">
                        {w.timestamp?.toMillis ? new Date(w.timestamp.toMillis()).toLocaleString() : 'N/A'}
                      </td>
                    </tr>
                  ))}
                  {allWallets.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-16 text-center text-[9px] font-black text-white/10 uppercase tracking-[0.6em]">No synced wallets detected</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10 h-auto lg:h-[700px] animate-in fade-in duration-700">
             <div className="glass-card overflow-hidden flex flex-col shadow-2xl h-[400px] lg:h-full">
                <div className="p-6 md:p-8 border-b border-white/5 bg-white/5 font-black text-[10px] md:text-[11px] text-white/20 uppercase tracking-[0.3em]">Active Dialogue</div>
                <div className="flex-1 overflow-y-auto divide-y divide-white/5 no-scrollbar">
                   {uniqueUserTickets.map(t => (
                    <button key={t.id} onClick={() => setActiveTicket(t)} className={`w-full p-6 md:p-8 text-left hover:bg-white/[0.02] flex items-center justify-between transition-all group ${activeTicket?.id === t.id ? 'bg-white/[0.04]' : ''}`}>
                      <div className="overflow-hidden">
                        <p className={`text-sm md:text-base font-black tracking-tight truncate transition-colors ${activeTicket?.id === t.id ? 'text-[#AEB877]' : 'text-white group-hover:text-[#AEB877]'}`}>{t.userName}</p>
                        <p className="text-[9px] md:text-[10px] font-black text-white/20 truncate mt-1 md:mt-1.5 uppercase tracking-widest">{t.lastMessage || 'Open Terminal'}</p>
                      </div>
                      {t.hasUnreadAdmin && <span className="w-2 md:w-2.5 h-2 md:h-2.5 rounded-full bg-[#FF4D4D] animate-pulse shrink-0 shadow-lg shadow-[#FF4D4D]/40"></span>}
                    </button>
                  ))}
                  {uniqueUserTickets.length === 0 && (
                    <div className="p-8 md:p-10 text-center">
                      <p className="text-[9px] md:text-[10px] font-black text-white/10 uppercase tracking-widest">No active threads</p>
                    </div>
                  )}
                </div>
             </div>
             <div className="lg:col-span-2 glass-card overflow-hidden flex flex-col relative shadow-2xl h-[500px] lg:h-full">
                {activeTicket ? (
                  <>
                    <div className="p-6 md:p-8 border-b border-white/5 bg-white/5 flex items-center justify-between">
                      <div>
                        <h3 className="font-black text-white text-base md:text-lg tracking-tight uppercase">{activeTicket.userName}</h3>
                        <p className="text-[10px] md:text-[11px] font-black text-white/20 mt-1 lowercase">{activeTicket.userEmail}</p>
                      </div>
                    </div>
                    <div className="flex-1 p-6 md:p-10 overflow-y-auto space-y-6 md:space-y-8 bg-gradient-to-b from-transparent to-[#AEB877]/[0.01] no-scrollbar">
                      {ticketMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.senderRole === 'admin' ? 'justify-end' : 'justify-start'} animate-in fade-in slide-in-from-bottom-4 duration-500`}>
                          <div className={`max-w-[85%] md:max-w-[75%] px-4 md:px-6 py-3 md:py-4 rounded-2xl md:rounded-3xl text-xs md:text-sm font-black border shadow-sm leading-relaxed ${
                            msg.senderRole === 'admin' 
                              ? 'bg-[#AEB877]/10 border-[#AEB877]/20 text-[#AEB877] rounded-tr-none' 
                              : 'bg-white/[0.03] border-white/5 text-white rounded-tl-none'
                          }`}>
                            {msg.text}
                          </div>
                        </div>
                      ))}
                      <div ref={chatEndRef} />
                    </div>
                    <div className="p-6 md:p-8 border-t border-white/5 bg-white/5 flex gap-3 md:gap-4">
                        <input 
                          type="text" 
                          value={replyInput} 
                          onChange={(e) => setReplyInput(e.target.value)} 
                          onKeyDown={(e) => e.key === 'Enter' && handleSendReply()} 
                          placeholder="Transmit reply..." 
                          className="flex-1 bg-black/40 border border-white/10 py-4 md:py-5 px-6 md:px-8 rounded-xl md:rounded-2xl text-white text-xs md:text-sm font-black outline-none focus:border-[#AEB877] shadow-inner transition-all" 
                        />
                        <button onClick={handleSendReply} className="bg-white text-black p-4 md:p-5 rounded-xl md:rounded-2xl hover:bg-[#AEB877] transition-all shadow-xl active:scale-95">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                        </button>
                    </div>
                  </>
                ) : (
                  <div className="flex-1 flex flex-col items-center justify-center space-y-4 opacity-20">
                    <div className="w-16 md:w-20 h-16 md:h-20 rounded-full border-2 border-dashed border-white/20 flex items-center justify-center">
                      <Icons.Support />
                    </div>
                    <p className="font-black uppercase text-[10px] md:text-[11px] tracking-[0.5em] text-white/20">Select Node Thread</p>
                  </div>
                )}
             </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-500">
           <div className={`w-full max-w-md bg-[#0a0a0b] border ${confirmModal.type === 'danger' ? 'border-[#FF4D4D]/30' : confirmModal.type === 'warning' ? 'border-amber-500/30' : 'border-[#AEB877]/30'} rounded-3xl md:rounded-[48px] p-8 md:p-12 text-center shadow-2xl relative overflow-hidden`}>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              <div className={`w-16 md:w-20 h-16 md:h-20 rounded-full flex items-center justify-center mx-auto mb-6 md:mb-8 text-xl md:text-2xl shadow-inner ${confirmModal.type === 'danger' ? 'bg-[#FF4D4D]/10 text-[#FF4D4D] border border-[#FF4D4D]/20' : confirmModal.type === 'warning' ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20' : confirmModal.type === 'success' ? 'bg-[#AEB877]/10 text-[#AEB877] border border-[#AEB877]/20' : 'bg-[#AEB877]/10 text-[#AEB877] border border-[#AEB877]/20'}`}>
                {confirmModal.type === 'danger' ? '⚠️' : confirmModal.type === 'warning' ? '🛑' : confirmModal.type === 'success' ? '✅' : 'ℹ️'}
              </div>
              <h2 className="text-xl md:text-2xl font-black text-white tracking-tight mb-3 md:mb-4 uppercase">{confirmModal.title}</h2>
              <p className="text-xs md:text-sm text-white/40 font-black uppercase tracking-widest leading-relaxed mb-8 md:mb-12 px-2 md:px-4">{confirmModal.message}</p>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                 <button onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))} className="py-4 md:py-5 bg-white/5 text-white/20 rounded-xl md:rounded-2xl font-black uppercase text-[10px] md:text-[11px] tracking-widest hover:text-white hover:bg-white/10 transition-all">Cancel</button>
                 <button onClick={confirmModal.onConfirm} className={`py-4 md:py-5 rounded-xl md:rounded-2xl font-black uppercase text-[10px] md:text-[11px] tracking-widest shadow-xl text-black transition-all active:scale-95 ${confirmModal.type === 'danger' ? 'bg-[#FF4D4D] text-white' : confirmModal.type === 'warning' ? 'bg-amber-600' : confirmModal.type === 'success' ? 'bg-[#AEB877]' : 'bg-white'}`}>Authorize</button>
              </div>
           </div>
        </div>
      )}

      {/* Success Modal */}
      {successMsg && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-500">
           <div className="w-full max-w-md bg-[#0a0a0b] border border-[#AEB877]/30 rounded-3xl md:rounded-[48px] p-10 md:p-14 text-center shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#AEB877]/20 to-transparent"></div>
              <div className="w-20 md:w-24 h-20 md:h-24 bg-[#AEB877]/10 border border-[#AEB877]/20 rounded-full flex items-center justify-center mx-auto mb-8 md:mb-10 text-[#AEB877] shadow-inner">
                 <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M20 6L9 17l-5-5"/></svg>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white tracking-tight mb-2 md:mb-3 uppercase">Protocol Success</h3>
              <p className="text-xs md:text-sm text-white/40 font-black uppercase tracking-widest leading-relaxed mb-10 md:mb-12 px-4 md:px-6">{successMsg}</p>
              <button onClick={closeSuccess} className="w-full py-5 md:py-6 bg-[#AEB877] text-black rounded-2xl md:rounded-3xl font-black uppercase text-[10px] md:text-xs tracking-[0.2em] shadow-xl shadow-[#AEB877]/20 transition-all hover:bg-white hover:scale-105 active:scale-95">Dismiss</button>
           </div>
        </div>
      )}

      {viewingKycDocs && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-500">
           <div className="w-full max-w-5xl bg-[#0a0a0b] border border-white/5 rounded-3xl md:rounded-[56px] p-6 md:p-16 relative shadow-2xl max-h-[95vh] overflow-y-auto no-scrollbar">
              <button onClick={() => setViewingKycDocs(null)} className="absolute top-6 md:top-10 right-6 md:top-10 text-white/20 hover:text-white transition-colors p-2 md:p-3 bg-white/5 rounded-xl md:rounded-2xl border border-white/5 hover:bg-white/10">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              <div className="mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight uppercase">Audit History</h2>
                <p className="text-[10px] md:text-[11px] text-white/20 font-black uppercase tracking-widest mt-1 md:mt-2">{viewingKycDocs.userName} • {viewingKycDocs.userEmail}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                 <div className="space-y-3 md:space-y-4">
                    <p className="text-[9px] md:text-[10px] font-black text-white/20 uppercase tracking-widest ml-2">Front Identification</p>
                        <div className="bg-black/40 border border-white/5 rounded-2xl md:rounded-[40px] p-2 md:p-3 aspect-video cursor-pointer group relative overflow-hidden shadow-inner" onClick={() => setInspectImage(viewingKycDocs.frontImage)}>
                        <img src={viewingKycDocs.frontImage} className="w-full h-full object-cover rounded-xl md:rounded-[32px] group-hover:scale-105 transition-transform duration-700" alt="Front" />
                        <div className="absolute inset-0 bg-[#AEB877]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                         <span className="bg-white text-black px-4 md:px-6 py-1.5 md:py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest">Enlarge</span>
                       </div>
                    </div>
                 </div>
                 <div className="space-y-3 md:space-y-4">
                    <p className="text-[9px] md:text-[10px] font-black text-white/20 uppercase tracking-widest ml-2">Back Identification</p>
                        <div className="bg-black/40 border border-white/5 rounded-2xl md:rounded-[40px] p-2 md:p-3 aspect-video cursor-pointer group relative overflow-hidden shadow-inner" onClick={() => setInspectImage(viewingKycDocs.backImage)}>
                        <img src={viewingKycDocs.backImage} className="w-full h-full object-cover rounded-xl md:rounded-[32px] group-hover:scale-105 transition-transform duration-700" alt="Back" />
                        <div className="absolute inset-0 bg-[#AEB877]/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                         <span className="bg-white text-black px-4 md:px-6 py-1.5 md:py-2 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-widest">Enlarge</span>
                       </div>
                    </div>
                 </div>
              </div>
           </div>
        </div>
      )}

      {inspectImage && (
        <div className="fixed inset-0 z-[700] bg-black/98 backdrop-blur-2xl flex items-center justify-center p-6 md:p-12 animate-in zoom-in duration-300" onClick={() => setInspectImage(null)}>
          <img src={inspectImage} className="max-w-full md:max-w-6xl max-h-full rounded-2xl md:rounded-[40px] border border-white/10 shadow-2xl" alt="Inspect" />
          <button className="absolute top-6 md:top-12 right-6 md:right-12 text-white/40 hover:text-white transition-colors">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
      )}

      {/* Equity Adjustment Modal */}
      {modalType === 'balance' && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-500">
           <div className={`w-full max-w-md bg-[#0a0a0b] border border-white/5 rounded-[32px] md:rounded-[48px] p-8 md:p-12 shadow-2xl relative overflow-hidden`}>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              <button onClick={() => setModalType(null)} className="absolute top-6 md:top-10 right-6 md:right-10 text-white/20 hover:text-white transition-colors p-2 md:p-3 bg-white/5 rounded-xl md:rounded-2xl border border-white/5 hover:bg-white/10">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              <h2 className="text-xl md:text-2xl font-black text-white tracking-tight mb-8 md:mb-10 text-center uppercase">Adjust Node Parameters</h2>
              <div className="space-y-4 md:space-y-6">
                <InputGroup label="Live Equity ($)" value={modalInput.live} onChange={(v:any) => setModalInput({...modalInput, live: v})} type="number" />
                <InputGroup label="Demo Equity ($)" value={modalInput.demo} onChange={(v:any) => setModalInput({...modalInput, demo: v})} type="number" />
                <InputGroup label="Individual Win Rate (0.0 - 1.0)" value={modalInput.winRate} onChange={(v:any) => setModalInput({...modalInput, winRate: v})} type="number" placeholder="e.g. 0.85 for 85%" />
                <button onClick={handleUpdateBalance} className="w-full py-4 md:py-6 bg-white text-black rounded-xl md:rounded-2xl font-black uppercase text-[10px] md:text-xs tracking-[0.2em] mt-4 md:mt-6 shadow-xl hover:bg-[#AEB877] transition-all active:scale-95">Commit Sync</button>
              </div>
           </div>
        </div>
      )}

      {/* Deployment Modal */}
      {modalType === 'addWallet' && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl animate-in fade-in duration-500">
           <div className={`w-full max-w-md bg-[#0a0a0b] border border-white/5 rounded-[48px] p-12 shadow-2xl relative overflow-hidden`}>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
              <button onClick={() => setModalType(null)} className="absolute top-10 right-10 text-white/20 hover:text-white transition-colors p-3 bg-white/5 rounded-2xl border border-white/5 hover:bg-white/10">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
              <h2 className="text-2xl font-black text-white tracking-tight mb-10 text-center uppercase">Deploy Node</h2>
              <div className="space-y-6">
                <InputGroup label="Asset Key (e.g. USDT_TRC20)" value={modalInput.key} onChange={(v:any) => setModalInput({...modalInput, key: v})} />
                <InputGroup label="Node Address" value={modalInput.address} onChange={(v:any) => setModalInput({...modalInput, address: v})} />
                <button onClick={handleAddWallet} className="w-full py-6 bg-white text-black rounded-2xl font-black uppercase text-xs tracking-[0.2em] mt-6 shadow-xl hover:bg-[#AEB877] transition-all active:scale-95">Commence Deployment</button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, icon, color = "#FFF", onClick }: any) => (
  <div onClick={onClick} className={`glass-card p-6 group relative overflow-hidden transition-all hover:border-[#AEB877]/20 shadow-2xl ${onClick ? 'cursor-pointer hover:bg-white/[0.04]' : ''}`}>
    <div className="absolute top-0 right-0 p-6 opacity-[0.05] text-5xl group-hover:scale-110 group-hover:opacity-[0.08] transition-all duration-700">{icon}</div>
    <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.2em] mb-1">{label}</p>
    <p className="text-2xl font-black tabular-nums tracking-tighter" style={{ color }}>{value}</p>
    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-[#AEB877]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
  </div>
);

const InputGroup = ({ label, value, onChange, type = "text", placeholder }: any) => (
  <div className="space-y-2">
    <label className="text-[9px] font-black text-white/20 uppercase tracking-widest ml-1">{label}</label>
    <input 
      type={type} 
      value={value} 
      onChange={e => onChange(e.target.value)} 
      placeholder={placeholder} 
      className="w-full bg-black/40 border border-white/10 rounded-xl py-3 px-5 text-white text-xs font-black focus:border-[#AEB877] outline-none transition-all shadow-inner placeholder:text-white/10" 
    />
  </div>
);

export default AdminPanel;